import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import DynamicTable from "../../../../../unicomponents/Tables/DynamicTable";
import {
  filterSessionsForSettingsScope,
  billingRecordMatch,
} from "../../../../../util/billing/normalizeBillingEntities";

function recordHasSavedBillingData(r) {
  if (!r) return false;
  const a = r.amount;
  if (a !== null && a !== undefined && a !== "") {
    if (typeof a === "number" && !Number.isNaN(a)) return true;
    const n = Number(a);
    if (!Number.isNaN(n)) return true;
  }
  if (String(r.bill_remark ?? "").trim() !== "") return true;
  if (String(r.internal_memo ?? "").trim() !== "") return true;
  return false;
}

/** IME(한글) 조합 중 부모 state와 value 동기화가 끊기지 않도록 로컬 문자열로 편집, 서버 반영은 onCommit */
function BillingMemoTextarea({
  className,
  value,
  onCommit,
  rows = 2,
  disabled = false,
}) {
  const [inner, setInner] = useState(() => (value == null ? "" : String(value)));
  const composingRef = useRef(false);

  useEffect(() => {
    if (!composingRef.current) {
      setInner(value == null ? "" : String(value));
    }
  }, [value]);

  return (
    <textarea
      className={className}
      rows={rows}
      value={inner}
      disabled={disabled}
      onCompositionStart={() => {
        composingRef.current = true;
      }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        const v = e.currentTarget.value;
        setInner(v);
        onCommit(v);
      }}
      onChange={(e) => {
        const v = e.target.value;
        setInner(v);
        if (!composingRef.current) {
          onCommit(v);
        }
      }}
    />
  );
}

export default function BillingRecordView({
  selectedItem,
  selectedSessionId,
  sessions,
  resolvedStudentList,
  billingRecords,
  getBillingRecord,
  updateBillingRecord,
  updateBillingRecordsBulk,
  sessionItems,
  settingData,
  handleToastCenterTop,
  canCreate,
  canUpdate,
  /** 페이지 진입에 사용; 있으면 입력 탭은 항상 수정 가능 */
  canAccess = false,
  /** false면 사이드바가 입력만 — can_access만으로도 입력 탭에서 신규·수정(upsert) 허용 */
  canRead = true,
}) {
  const { t } = useTranslation();
  const canMutate =
    Boolean(canAccess) ||
    Boolean(canCreate || canUpdate) ||
    !canRead;
  const [localEdits, setLocalEdits] = useState({});
  const [prevSchoolYearId, setPrevSchoolYearId] = useState("");
  const [prevCourse, setPrevCourse] = useState("");
  const [prevSessionId, setPrevSessionId] = useState("");

  useEffect(() => {
    setLocalEdits({});
  }, [selectedItem?.id]);

  const schoolYearIdsOnSessions = useMemo(() => {
    const set = new Set(
      (sessions || []).map((s) => String(s.school_year_id)).filter(Boolean),
    );
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [sessions]);

  const schoolYearLabel = (yearIdStr) => {
    const row = settingData?.find(
      (s) => s.hierarchy_level === 0 && String(s.id) === String(yearIdStr),
    );
    return row ? `${row.school_year}` : yearIdStr;
  };

  const prevCourseOptions = useMemo(() => {
    if (!settingData || !prevSchoolYearId) return [];
    const yearEntry = settingData.find(
      (s) =>
        s.hierarchy_level === 0 && String(s.id) === String(prevSchoolYearId),
    );
    if (!yearEntry) return [];
    return settingData.filter(
      (s) => s.hierarchy_level === 1 && s.parent_id === yearEntry.id,
    );
  }, [settingData, prevSchoolYearId]);

  const prevSessionOptions = useMemo(() => {
    if (!prevSchoolYearId || !prevCourse) return [];
    return filterSessionsForSettingsScope(
      sessions,
      prevSchoolYearId,
      prevCourse,
    ).filter((s) => s.id !== selectedSessionId);
  }, [sessions, prevSchoolYearId, prevCourse, selectedSessionId]);

  const studentRows = useMemo(() => {
    return resolvedStudentList.map((student) => {
      const record = getBillingRecord(
        selectedSessionId,
        student.username,
        selectedItem.id,
      );
      const local = localEdits[student.username];
      return {
        ...student,
        id: student.username,
        amount:
          local?.amount !== undefined ? local.amount : (record?.amount ?? ""),
        bill_remark:
          local?.bill_remark !== undefined
            ? local.bill_remark
            : (record?.bill_remark ?? ""),
        internal_memo:
          local?.internal_memo !== undefined
            ? local.internal_memo
            : (record?.internal_memo ?? ""),
        isDirty: !!local,
      };
    });
  }, [
    resolvedStudentList,
    selectedSessionId,
    selectedItem,
    localEdits,
    getBillingRecord,
  ]);

  const handleLocalChange = useCallback((username, field, value) => {
    setLocalEdits((prev) => ({
      ...prev,
      [username]: { ...prev[username], [field]: value },
    }));
  }, []);

  const handleSaveOne = useCallback(
    (username) => {
      if (!canMutate) {
        handleToastCenterTop(
          t("billingManagement.permissions.noSave") || "저장 권한이 없습니다.",
        );
        return;
      }
      const local = localEdits[username];
      if (!local) return;
      const existing = getBillingRecord(
        selectedSessionId,
        username,
        selectedItem.id,
      );
      updateBillingRecord(selectedSessionId, username, selectedItem.id, {
        amount:
          local.amount !== undefined
            ? local.amount === ""
              ? null
              : Number(local.amount)
            : (existing?.amount ?? null),
        bill_remark:
          local.bill_remark !== undefined
            ? String(local.bill_remark).trim()
            : (existing?.bill_remark ?? ""),
        internal_memo:
          local.internal_memo !== undefined
            ? String(local.internal_memo).trim()
            : (existing?.internal_memo ?? ""),
      });
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[username];
        return next;
      });
      handleToastCenterTop(
        t("billingManagement.messages.saved") || "저장되었습니다.",
      );
    },
    [
      localEdits,
      selectedSessionId,
      selectedItem,
      updateBillingRecord,
      getBillingRecord,
      handleToastCenterTop,
      canMutate,
      t,
    ],
  );

  const handleSaveAll = useCallback(() => {
    if (!canMutate) {
      handleToastCenterTop(
        t("billingManagement.permissions.noSave") || "저장 권한이 없습니다.",
      );
      return;
    }
    const records = [];
    resolvedStudentList.forEach((student) => {
      const local = localEdits[student.username];
      if (!local) return;
      const existing = getBillingRecord(
        selectedSessionId,
        student.username,
        selectedItem.id,
      );
      records.push({
        session_id: selectedSessionId,
        username: student.username,
        item_id: selectedItem.id,
        amount:
          local.amount !== undefined
            ? local.amount === ""
              ? null
              : Number(local.amount)
            : (existing?.amount ?? null),
        bill_remark:
          local.bill_remark !== undefined
            ? String(local.bill_remark).trim()
            : (existing?.bill_remark ?? ""),
        internal_memo:
          local.internal_memo !== undefined
            ? String(local.internal_memo).trim()
            : (existing?.internal_memo ?? ""),
      });
    });

    if (records.length === 0) {
      handleToastCenterTop(
        t("billingManagement.messages.noChanges") || "변경사항이 없습니다.",
      );
      return;
    }
    updateBillingRecordsBulk(records);
    setLocalEdits({});
    handleToastCenterTop(
      t("billingManagement.messages.savedCount", { count: records.length }) ||
        `${records.length}건이 저장되었습니다.`,
    );
  }, [
    localEdits,
    resolvedStudentList,
    selectedSessionId,
    selectedItem,
    getBillingRecord,
    updateBillingRecordsBulk,
    handleToastCenterTop,
    canMutate,
    t,
  ]);

  const handleLoadPreviousData = useCallback(() => {
    if (!canMutate) {
      handleToastCenterTop(
        t("billingManagement.permissions.noLoad") || "불러오기 권한이 없습니다.",
      );
      return;
    }
    if (!prevSessionId) {
      handleToastCenterTop(
        t("billingManagement.messages.selectPastPeriod") ||
          "지난 기간을 선택해주세요.",
      );
      return;
    }
    const itemLabel = String(selectedItem?.item_name || "").trim();
    const candidates = sessionItems.filter(
      (si) => String(si.billing_session_id) === String(prevSessionId),
    );
    const sourceItem = candidates.find(
      (si) => String(si.item_name || "").trim() === itemLabel,
    );

    if (!sourceItem) {
      handleToastCenterTop(
        t("billingManagement.messages.noMatchingItemInPast", {
          name: itemLabel,
        }) || `이전 데이터에 "${itemLabel}"라는 이름의 항목이 없습니다.`,
      );
      return;
    }

    const prevItemRecords = billingRecords.filter(
      (r) =>
        billingRecordMatch(r, prevSessionId, r.username, sourceItem.id),
    );

    const hasAnySaved = prevItemRecords.some(recordHasSavedBillingData);
    if (!hasAnySaved) {
      handleToastCenterTop(
        t("billingManagement.messages.noSavedDataForItem", {
          name: itemLabel,
        }) ||
          `이전 데이터의 "${itemLabel}" 항목에 저장된 데이터가 없습니다.`,
      );
      return;
    }

    setLocalEdits((prev) => {
      const next = { ...prev };
      resolvedStudentList.forEach((student) => {
        const src = getBillingRecord(
          prevSessionId,
          student.username,
          sourceItem.id,
        );
        next[student.username] = {
          ...(next[student.username] || {}),
          amount:
            src && src.amount != null && src.amount !== ""
              ? src.amount
              : "",
          bill_remark: src ? (src.bill_remark ?? "") : "",
          internal_memo: src ? (src.internal_memo ?? "") : "",
        };
      });
      return next;
    });
  }, [
    prevSessionId,
    selectedItem,
    sessionItems,
    resolvedStudentList,
    billingRecords,
    getBillingRecord,
    handleToastCenterTop,
    canMutate,
    t,
  ]);

  const handleApplyDefaultAmount = useCallback(() => {
    if (!canMutate) {
      handleToastCenterTop(
        t("billingManagement.messages.noApplyPermission") ||
          "적용 권한이 없습니다.",
      );
      return;
    }
    if (selectedItem?.default_amount == null) return;
    setLocalEdits((prev) => {
      const next = { ...prev };
      resolvedStudentList.forEach((student) => {
        next[student.username] = {
          ...(next[student.username] || {}),
          amount: selectedItem.default_amount,
        };
      });
      return next;
    });
    // handleToastCenterTop("기본값을 전체 학생에게 적용했습니다.");
  }, [resolvedStudentList, selectedItem, canMutate, handleToastCenterTop, t]);

  const dirtyCount = Object.keys(localEdits).length;

  const tableConfig = useMemo(
    () => ({
      style: { fontSize: "13px", tableLayout: "auto" },
      headers: [
        {
          headerKey: "hierarchyOne",
          label: t("common.labels.schoolStage") || "과정",
          style: { minWidth: "70px", maxWidth: "80px" },
          sortField: "hierarchyOne",
        },
        {
          headerKey: "hierarchyTwo",
          label: t("common.labels.studentYear") || "학년",
          style: { minWidth: "70px", maxWidth: "80px" },
          sortField: "hierarchyTwo",
        },
        {
          headerKey: "hierarchyThree",
          label: t("common.labels.class") || "반",
          style: { minWidth: "70px", maxWidth: "80px" },
          sortField: "hierarchyThree",
        },
        {
          headerKey: "fullname",
          label: t("common.user.student") || "학생",
          style: { minWidth: "70px", maxWidth: "100px" },
          sortField: "fullname",
        },
        {
          headerKey: "amount",
          label:
            t("billingManagement.labels.itemAmount", {
              name: selectedItem.item_name,
            }) || `${selectedItem.item_name} 금액`,
          style: { minWidth: "100px", maxWidth: "150px" },
          sortField: "amount",
        },
        {
          headerKey: "bill_remark",
          label: t("billingManagement.labels.billMemo") || "고지서용 메모",
          style: { minWidth: "100px", maxWidth: "150px" },
        },
        {
          headerKey: "internal_memo",
          label: t("billingManagement.labels.internalMemo") || "내부용 메모",
          style: { minWidth: "100px", maxWidth: "150px" },
        },
        {
          headerKey: "save",
          label: t("billingManagement.labels.save") || "저장",
          style: { minWidth: "60px", maxWidth: "70px" },
        },
      ],
      columns: [
        { valueKey: "hierarchyOne" },
        { valueKey: "hierarchyTwo" },
        { valueKey: "hierarchyThree" },
        { valueKey: "fullname" },
        {
          valueKey: "amount",
          valueFormatter: (val, item) => (
            <input
              type="number"
              className="form-control form-control-sm text-end"
              value={item.amount}
              disabled={!canMutate}
              onChange={(e) =>
                handleLocalChange(item.username, "amount", e.target.value)
              }
            />
          ),
        },
        {
          valueKey: "bill_remark",
          cellStyle: { verticalAlign: "top" },
          valueFormatter: (val, item) => (
            <BillingMemoTextarea
              className="form-control form-control-sm"
              rows={2}
              value={item.bill_remark}
              disabled={!canMutate}
              onCommit={(v) =>
                handleLocalChange(item.username, "bill_remark", v)
              }
            />
          ),
        },
        {
          valueKey: "internal_memo",
          cellStyle: { verticalAlign: "top" },
          valueFormatter: (val, item) => (
            <BillingMemoTextarea
              className="form-control form-control-sm"
              rows={2}
              value={item.internal_memo}
              disabled={!canMutate}
              onCommit={(v) =>
                handleLocalChange(item.username, "internal_memo", v)
              }
            />
          ),
        },
        {
          valueKey: "isDirty",
          valueFormatter: (val, item) => (
            <button
              className={`btn btn-sm px-2 m-0 ${item.isDirty ? "btn-primary" : "btn-outline-secondary"}`}
              style={{ fontSize: "11px" }}
              onClick={() => handleSaveOne(item.username)}
              disabled={!canMutate || !item.isDirty}
            >
              {t("billingManagement.labels.save") || "저장"}
            </button>
          ),
          cellStyle: {
            textAlign: "center",
            verticalAlign: "middle",
            padding: 0,
          },
        },
      ],
      rows: studentRows,
    }),
    [
      studentRows,
      selectedItem,
      handleLocalChange,
      handleSaveOne,
      canMutate,
      t,
    ],
  );

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="text-muted small">
          {t("billingManagement.labels.loadPreviousData") ||
            "이전 데이터 불러오기:"}
        </span>
        <select
          className="form-select form-select-sm"
          style={{ width: 100 }}
          value={prevSchoolYearId}
          disabled={!canMutate}
          onChange={(e) => {
            setPrevSchoolYearId(e.target.value);
            setPrevCourse("");
            setPrevSessionId("");
          }}
        >
          <option value="">
            {t("billingManagement.labels.schoolYearOption") || "학년도"}
          </option>
          {schoolYearIdsOnSessions.map((yid) => (
            <option key={yid} value={yid}>
              {schoolYearLabel(yid)}
            </option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          style={{ width: 100 }}
          value={prevCourse}
          onChange={(e) => {
            setPrevCourse(e.target.value);
            setPrevSessionId("");
          }}
          disabled={!canMutate || !prevSchoolYearId}
        >
          <option value="">
            {t("billingManagement.labels.courseOption") || "과정"}
          </option>
          <option value="__INTEGRATED__">
            {t("billingManagement.labels.integrated") || "통합"}
          </option>
          {prevCourseOptions.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.group_name}
            </option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          style={{ width: 100 }}
          value={prevSessionId}
          onChange={(e) => setPrevSessionId(e.target.value)}
          disabled={!canMutate || !prevCourse}
        >
          <option value="">
            {t("billingManagement.labels.periodOption") || "기간"}
          </option>
          {prevSessionOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.session_name}
            </option>
          ))}
        </select>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={handleLoadPreviousData}
          disabled={!canMutate || !prevSessionId}
        >
          {t("billingManagement.labels.load") || "불러오기"}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleApplyDefaultAmount}
          disabled={!canMutate}
        >
          {t("billingManagement.labels.applyDefaultAll") || "기본값 전체 적용"}
        </button>
        <button
          className="btn btn-primary btn-sm ms-auto"
          onClick={handleSaveAll}
          disabled={!canMutate || dirtyCount === 0}
        >
          {t("billingManagement.labels.saveAll") || "모두저장"}{" "}
          {dirtyCount > 0 && `(${dirtyCount})`}
        </button>
      </div>

      <DynamicTable
        tableConfig={tableConfig}
        dataStructure="flat"
        tableStyle="cleanModern"
        sortable={true}
        initialSortField="hierarchyOne"
        initialSortDirection="asc"
        searchable={true}
        searchPlaceholder={
          t("billingManagement.listPage.searchStudents") || "학생 검색..."
        }
        searchFields={["fullname", "hierarchyOne"]}
        pagination={studentRows.length > 50}
        pageSize={50}
      />
    </div>
  );
}
